// @spec SHL-STR-022
// @spec SHL-STR-046

/**
 * Keyboard shortcut interface defining a shortcut configuration.
 */
export interface KeyboardShortcut {
  /** Unique identifier for the shortcut */
  id: string;
  /** Key combination (react-hotkeys-hook format, e.g., "ctrl+k", "shift+/") */
  keys: string | string[];
  /** Human-readable description */
  description: string;
  /** Category for grouping */
  category: ShortcutCategory;
  /** Scopes where this shortcut is active */
  scopes?: string[];
  /** Whether to show in help dialog */
  showInHelp?: boolean;
}

/**
 * Categories for organizing keyboard shortcuts.
 */
export type ShortcutCategory = 'navigation' | 'search' | 'actions' | 'editing' | 'help';

import type { TranslateFn } from '@/lib/i18n';

/**
 * Display name mapping for shortcut categories (English fallback).
 * Prefer `getShortcutCategoryLabel(category, t)` for translated output.
 */
export const SHORTCUT_CATEGORY_LABELS: Record<ShortcutCategory, string> = {
  navigation: 'Navigation',
  search: 'Search',
  actions: 'Actions',
  editing: 'Editing',
  help: 'Help',
};

/**
 * Get a translated category label.
 */
export function getShortcutCategoryLabel(category: ShortcutCategory, t?: TranslateFn): string {
  if (t) {
    return t(`shortcuts.categories.${category}`);
  }
  return SHORTCUT_CATEGORY_LABELS[category];
}

/**
 * Order in which categories should be displayed.
 */
export const SHORTCUT_CATEGORY_ORDER: ShortcutCategory[] = [
  'help',
  'navigation',
  'search',
  'actions',
  'editing',
];

/**
 * Centralized registry of all keyboard shortcuts in the application.
 *
 * When adding new shortcuts:
 * 1. Define them here with a unique ID
 * 2. Set `showInHelp: true` to display in the shortcuts dialog
 * 3. Assign appropriate category and scopes
 * 4. Use the shortcut in your component with `useHotkeys`
 */
export const KEYBOARD_SHORTCUTS: KeyboardShortcut[] = [
  // Help
  {
    id: 'show-shortcuts',
    keys: ['shift+/', '?'],
    description: 'Show keyboard shortcuts',
    category: 'help',
    scopes: ['global'],
    showInHelp: true,
  },
  {
    id: 'close-dialog',
    keys: 'escape',
    description: 'Close dialog / Cancel',
    category: 'navigation',
    scopes: ['*'],
    showInHelp: true,
  },
  // Navigation
  {
    id: 'go-home',
    keys: ['g h', 'alt+h'],
    description: 'Go to Dashboard',
    category: 'navigation',
    scopes: ['global'],
    showInHelp: true,
  },
  {
    id: 'go-settings',
    keys: ['g s', 'alt+s'],
    description: 'Go to Settings',
    category: 'navigation',
    scopes: ['global'],
    showInHelp: true,
  },
  {
    id: 'focus-navigation',
    keys: 'g>n',
    description: 'Focus navigation sidebar',
    category: 'navigation',
    scopes: ['global'],
    showInHelp: true,
  },
  {
    id: 'focus-main',
    keys: 'g>m',
    description: 'Focus main content',
    category: 'navigation',
    scopes: ['global'],
    showInHelp: true,
  },
  // Search
  {
    id: 'global-search',
    keys: ['ctrl+k', 'mod+k'],
    description: 'Open global search',
    category: 'search',
    scopes: ['global'],
    showInHelp: true,
  },
  {
    id: 'command-palette',
    keys: ['ctrl+shift+p', 'mod+shift+p'],
    description: 'Open command palette',
    category: 'search',
    scopes: ['global'],
    showInHelp: true,
  },
  // Actions
  {
    id: 'save',
    keys: ['ctrl+s', 'mod+s'],
    description: 'Save current form',
    category: 'actions',
    scopes: ['editing'],
    showInHelp: true,
  },
  {
    id: 'new-item',
    keys: ['ctrl+n', 'mod+n'],
    description: 'Create new item',
    category: 'actions',
    scopes: ['global'],
    showInHelp: true,
  },
];

/**
 * Get shortcuts filtered for display in the help dialog.
 */
export function getVisibleShortcuts(): KeyboardShortcut[] {
  return KEYBOARD_SHORTCUTS.filter((shortcut) => shortcut.showInHelp);
}

/**
 * Get shortcuts grouped by category.
 */
export function getShortcutsByCategory(): Map<ShortcutCategory, KeyboardShortcut[]> {
  const grouped = new Map<ShortcutCategory, KeyboardShortcut[]>();

  for (const shortcut of getVisibleShortcuts()) {
    const existing = grouped.get(shortcut.category) ?? [];
    grouped.set(shortcut.category, [...existing, shortcut]);
  }

  return grouped;
}

/**
 * Get a shortcut by ID.
 */
export function getShortcutById(id: string): KeyboardShortcut | undefined {
  return KEYBOARD_SHORTCUTS.find((shortcut) => shortcut.id === id);
}

/**
 * Utility function for OS detection.
 * Uses userAgentData when available, falls back to userAgent.
 */
export function isMac(): boolean {
  if (typeof window === 'undefined') return false;

  // Modern approach using User-Agent Client Hints
  // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access
  const userAgentData = (navigator as any).userAgentData as { platform?: string } | undefined;
  if (userAgentData?.platform) {
    return userAgentData.platform.toLowerCase() === 'macos';
  }

  // Fallback to userAgent parsing
  return navigator.userAgent.includes('Mac');
}

/**
 * Key symbol mapping for display purposes.
 * Mac uses symbols, other platforms use text.
 */
export const KEY_SYMBOLS = {
  ctrl: { mac: '⌘', other: 'Ctrl' },
  mod: { mac: '⌘', other: 'Ctrl' },
  alt: { mac: '⌥', other: 'Alt' },
  shift: { mac: '⇧', other: 'Shift' },
  enter: { mac: '↵', other: 'Enter' },
  backspace: { mac: '⌫', other: 'Backspace' },
  escape: { mac: 'Esc', other: 'Esc' },
  space: { mac: 'Space', other: 'Space' },
  tab: { mac: 'Tab', other: 'Tab' },
} as const;

/**
 * Format a key combination for display.
 * Handles modifier keys and OS-specific symbols.
 *
 * @param keys - Key combination string (e.g., "ctrl+k", "shift+/")
 * @returns Formatted key parts for display
 */
export function formatKeyCombo(keys: string): string[] {
  const isMacOS = isMac();
  const parts = keys.toLowerCase().split('+');

  return parts.map((part) => {
    const trimmed = part.trim();

    // Check if it's a known modifier/special key
    const symbolKey = trimmed as keyof typeof KEY_SYMBOLS;
    if (symbolKey in KEY_SYMBOLS) {
      const symbol = KEY_SYMBOLS[symbolKey];
      return isMacOS ? symbol.mac : symbol.other;
    }

    // For regular keys, uppercase single characters
    if (trimmed.length === 1) {
      return trimmed.toUpperCase();
    }

    // Handle special characters
    if (trimmed === '/') return '/';
    if (trimmed === '?') return '?';

    // Capitalize first letter for multi-character keys
    return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
  });
}
